/// <reference path="../../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />
import {ITranscriptionManager, ITranscription } from '../interfaces/ITranscriptionManager'
import { LogEvents } from '../../utils/LogEvents';
import { DurationMeasureType } from '../interfaces/ILogEvent'
import { AddTranscriptionToDocumentKeys } from './telemetry/OfficeJSKeys';
import { ITranscriptionSourceFile } from '../interfaces/ITranscriptionSourceFileProvider';
import { GetSpeakerName } from '../../frontend/actions/TranscribingActions';
import { ErrorStringFromException } from '../../utils/utils';
import { FormatTime } from '../../frontend/utils';
import { ENABLE_RICH_API_TESTCODE } from '../../AppConstants';

interface AddToDocumentOptions {
  withTimestamps: boolean;
  withSpeakers: boolean
}

export async function AddTranscriptionToDocument(transcription: ITranscription, sourceFile: ITranscriptionSourceFile | null, addOption: AddToDocumentOptions) {
  LogEvents.OfficeJS.Start();
  LogEvents.OfficeJS.BeginDurationMeasure(DurationMeasureType.Normal, AddTranscriptionToDocumentKeys.AddTranscriptionToDocumentDuration);

  try {
    let characterCount = 0;

    await Word.run(async (context) => {
        const currentSelectionRange: Word.Range = context.document.getSelection();
        let transcriptSectionStartRange = currentSelectionRange;
      
        if (sourceFile != null) {
          // Insert audio file section
          const audioFileHeadingRange: Word.Range = currentSelectionRange.insertText(TranscriptionStrings.InDocumentTranscriptAudioFileHeading, Word.InsertLocation.end);
          characterCount += TranscriptionStrings.InDocumentTranscriptAudioFileHeading.length;
          audioFileHeadingRange.styleBuiltIn = 'Heading1';
      
          const breakRangeAfterAudioFileHeading = audioFileHeadingRange.insertText('\n', Word.InsertLocation.end);
          breakRangeAfterAudioFileHeading.styleBuiltIn = 'Normal';
          characterCount += 1;

          const hyperlinkRange = breakRangeAfterAudioFileHeading.insertText(sourceFile.fileName, Word.InsertLocation.end);
          characterCount += sourceFile.fileName.length;
          hyperlinkRange.hyperlink = sourceFile.permanentAudioUrl; 
          const breakRangeAfterHyperlink = hyperlinkRange.insertText('\n', Word.InsertLocation.after);
          transcriptSectionStartRange = breakRangeAfterHyperlink;
        }

        // Insert transcript section
        const headingRange: Word.Range = transcriptSectionStartRange.insertText(TranscriptionStrings.InDocumentTranscriptTranscriptHeading, Word.InsertLocation.after);
        characterCount += TranscriptionStrings.InDocumentTranscriptTranscriptHeading.length;
        headingRange.styleBuiltIn = 'Heading1';

        const breakRange = headingRange.insertText('\n', Word.InsertLocation.end);
        breakRange.styleBuiltIn = 'Normal';
        characterCount += 1;

        let textToInsert: string = ''; 
        
        for (const segment of transcription.transcriptSegments) {
          if (addOption.withTimestamps) {
            textToInsert +=  FormatTime(segment.start); 
            if (!addOption.withSpeakers) {
              textToInsert += '\n';
            }
          }
          if (addOption.withSpeakers) {
            textToInsert +=  (segment.speakerId != null ? ' ' + GetSpeakerName(segment.speakerId, transcription.speakerNames) : '')  + '\n'; 
          }
          textToInsert += segment.text + '\n';
          characterCount += segment.text.length + 1;
        } 
        breakRange.insertText(textToInsert, Word.InsertLocation.end);
        breakRange.select(Word.SelectionMode.end);
    });
    LogEvents.OfficeJS.End([{ key: AddTranscriptionToDocumentKeys.CharacterCount, value: characterCount }, {key: AddTranscriptionToDocumentKeys.AddTranscriptionToDocumentOptionWithTimestamps , value: addOption.withTimestamps }, {key: AddTranscriptionToDocumentKeys.AddTranscriptionToDocumentOptionWithSpeakers , value: addOption.withSpeakers }]);
  } catch (e) {
    LogEvents.OfficeJS.End([{key: AddTranscriptionToDocumentKeys.FailureReason, value: ErrorStringFromException(e)}]);
    throw e;
  }
}

export async function AddTranscriptionSegmentToDocument(transcriptionManager: ITranscriptionManager, segmentIndex: number) {
  LogEvents.OfficeJS.Start();
  LogEvents.OfficeJS.BeginDurationMeasure(DurationMeasureType.Normal, AddTranscriptionToDocumentKeys.AddTranscriptionSegmentToDocumentDuration);

  if(ENABLE_RICH_API_TESTCODE){
    var id = "ridTranscriptionQuotaMinutesUsed";
    var loadString = "value";
    let val = await OfficeCore.run(function(ctx) {
      var setting = ctx.firstParty.roamingSettings.getItem(id).load(loadString);
      return ctx.sync().then(function() {
        return setting;
      });
    });
    console.log("Got 1st getTranscriptionQuota Value ::"+val);

    let testAsyncSet: any;
    testAsyncSet = false;
    let setJson: any;
    if(testAsyncSet){
      setJson = 1;
      await OfficeCore.run(function(ctx) {
        var setting = ctx.firstParty.roamingSettings.getItem(id).load(loadString);
        return ctx.sync().then(async function() {
          setting.value = setJson;
          await ctx.sync();
        });
      });
      console.log("Set getTranscriptionQuota Value");
    } else {
      setJson = 2;
      await OfficeCore.run(function(ctx) {
        var setting = ctx.firstParty.roamingSettings.getItem(id).load(loadString);
        return ctx.sync().then(function() {
          setting.value = setJson;
          ctx.sync();
        });
      });
    }

    val = await OfficeCore.run(function(ctx) {
      var setting = ctx.firstParty.roamingSettings.getItem(id).load(loadString);
      return ctx.sync().then(function() {
        return setting;
      });
    });
    console.log("Get 2nd getTranscriptionQuota Value ::"+val);
  }

  try {
    let characterCount = 0;

    const segment = transcriptionManager.GetTranscription().transcriptSegments[segmentIndex];
    await Word.run(async (context) => {
        const currentSelectionRange: Word.Range = context.document.getSelection();
        const segmentRange: Word.Range = currentSelectionRange.insertText(FormatTime(segment.start) + (segment.speakerId != null ? ' ' + GetSpeakerName(segment.speakerId, transcriptionManager.GetTranscription().speakerNames) : '') + '\n' + segment.text + '\n', Word.InsertLocation.end);
        characterCount += segment.text.length + 1;
        segmentRange.select(Word.SelectionMode.end);
    });
    LogEvents.OfficeJS.End([{ key: AddTranscriptionToDocumentKeys.CharacterCount, value: characterCount }]);
  } catch (e) {
    LogEvents.OfficeJS.End([{key: AddTranscriptionToDocumentKeys.FailureReason, value: ErrorStringFromException(e)}]);
    throw e;
  }
}